import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import { enqueueSnackbar } from 'notistack';
import {
  ExportHubAPI,
  ExportProduct,
  ProductStructure,
  ProductStructureNode,
} from 'api/ExportHubAPI';
import { TierBadge, StatusPill } from './productPresets';
import BomLinkModal from './BomLinkModal';

interface Props {
  product: ExportProduct;
  groupId: string | undefined;
  onChanged: () => void;
}

const stripeClass = (status: string): string =>
  status === 'Restricted'
    ? 'eh-struct-danger'
    : status === 'Action Required'
    ? 'eh-struct-warn'
    : '';

const ProductStructurePanel: React.FC<Props> = ({
  product,
  groupId,
  onChanged,
}) => {
  const history = useHistory();
  const [structure, setStructure] = useState<ProductStructure>({
    children: [],
    parents: [],
    child_issue_count: 0,
  });
  const [modalMode, setModalMode] = useState<'child' | 'parent' | null>(null);

  const load = useCallback(async () => {
    if (!groupId) return;
    try {
      setStructure(
        await ExportHubAPI.getProductStructure(product.id, groupId),
      );
    } catch (err) {
      console.error('Failed to load structure:', err);
    }
  }, [product.id, groupId]);

  useEffect(() => {
    load();
  }, [load]);

  const canHaveChildren =
    product.tier === 'Assembly' || product.tier === 'Sub-assembly';
  const canHaveParents =
    product.tier === 'Component' || product.tier === 'Sub-assembly';

  const removeChild = async (childId: string) => {
    if (!groupId) return;
    try {
      await ExportHubAPI.removeBomLink(product.id, childId, groupId);
      enqueueSnackbar('Child removed.', { variant: 'success' });
      load();
      onChanged();
    } catch (err) {
      enqueueSnackbar('Failed to remove child.', { variant: 'error' });
    }
  };
  const removeParent = async (parentId: string) => {
    if (!groupId) return;
    try {
      await ExportHubAPI.removeBomLink(parentId, product.id, groupId);
      enqueueSnackbar('Removed from assembly.', { variant: 'success' });
      load();
      onChanged();
    } catch (err) {
      enqueueSnackbar('Failed to remove parent.', { variant: 'error' });
    }
  };

  const openProduct = (id: string) =>
    history.push(`/export-hub/databases/${product.database_id}/products/${id}`);

  const renderNode = (
    n: ProductStructureNode,
    onRemove: () => void,
  ): React.ReactNode => (
    <div className={`eh-struct-row ${stripeClass(n.compliance_status)}`} key={n.id}>
      <button
        className='eh-struct-main'
        type='button'
        onClick={() => openProduct(n.id)}
      >
        <span className='eh-struct-name'>
          {n.name}
          {n.quantity ? <span className='eh-struct-qty'>× {n.quantity}</span> : null}
        </span>
        <span className='eh-struct-meta'>
          <TierBadge value={n.tier} />
          <StatusPill value={n.compliance_status} />
        </span>
      </button>
      <button
        className='eh-linked-x'
        type='button'
        onClick={onRemove}
        aria-label='Remove link'
      >
        <CloseIcon style={{ fontSize: 14 }} />
      </button>
    </div>
  );

  return (
    <div className='eh-panel'>
      <div className='eh-panel-title eh-panel-title-flex'>
        <span>
          <AccountTreeOutlinedIcon
            style={{ fontSize: 16, marginRight: 6, verticalAlign: 'middle' }}
          />
          Product Structure
        </span>
        {structure.child_issue_count > 0 && (
          <span className='eh-child-issues'>
            <WarningAmberIcon style={{ fontSize: 14 }} />
            {structure.child_issue_count} child issue
            {structure.child_issue_count === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {/* Part of / Where used (parents) */}
      {canHaveParents && (
        <div className='eh-struct-section'>
          <div className='eh-struct-section-head'>
            <span className='eh-struct-section-title'>
              {product.tier === 'Component' ? 'Where used' : 'Part of'}
            </span>
            <button
              className='eh-mini-btn'
              type='button'
              onClick={() => setModalMode('parent')}
            >
              <AddIcon style={{ fontSize: 15 }} /> Add parent
            </button>
          </div>
          {structure.parents.length === 0 && (
            <div className='eh-detail-note'>Not part of any assembly.</div>
          )}
          {structure.parents.map((n) =>
            renderNode(n, () => removeParent(n.id)),
          )}
        </div>
      )}

      {/* Contains (children) */}
      {canHaveChildren && (
        <div className='eh-struct-section'>
          <div className='eh-struct-section-head'>
            <span className='eh-struct-section-title'>Contains</span>
            <button
              className='eh-mini-btn'
              type='button'
              onClick={() => setModalMode('child')}
            >
              <AddIcon style={{ fontSize: 15 }} /> Add child
            </button>
          </div>
          {structure.children.length === 0 && (
            <div className='eh-detail-note'>No child products yet.</div>
          )}
          {structure.children.map((n) =>
            renderNode(n, () => removeChild(n.id)),
          )}
        </div>
      )}

      {!canHaveChildren && !canHaveParents && (
        <div className='eh-detail-note'>
          This product&rsquo;s tier has no hierarchy relationships.
        </div>
      )}

      {modalMode && (
        <BomLinkModal
          open
          mode={modalMode}
          product={product}
          databaseId={product.database_id || ''}
          groupId={groupId}
          excludeIds={[
            ...structure.children.map((c) => c.id),
            ...structure.parents.map((p) => p.id),
          ]}
          onClose={() => setModalMode(null)}
          onLinked={() => {
            setModalMode(null);
            load();
            onChanged();
          }}
        />
      )}
    </div>
  );
};

export default ProductStructurePanel;
